/**
 * OIDC authentication against our MAS (MSC3861).
 *
 * Why this file exists at all: our homeserver has no password login endpoint —
 * `/_matrix/client/v3/login` answers M_UNRECOGNIZED (verified on the spike,
 * 2026-09-09). OIDC is the only way in, so this is the first thing that must work.
 *
 * NOTE ON IMPORTS: `OAuth2` and `TokenRefresher` are not part of matrix-js-sdk's
 * public API surface; we reach into `matrix-js-sdk/lib/oauth/*`. The version in
 * package.json is therefore pinned exactly. Re-run the login test on every bump.
 */
import * as sdk from "matrix-js-sdk";
import { OAuth2 } from "matrix-js-sdk/lib/oauth/index.js";
import { TokenRefresher } from "matrix-js-sdk/lib/oauth/tokenRefresher.js";

import { config, isNativeShell, serverStoragePrefix } from "../config";
import { serveDeviceDisplayName } from "../matrix/profile";
import { loadSession, saveSession, clearSession, type StoredSession } from "./session";

/**
 * Ключи хранилища ПРИВЯЗАНЫ К СЕРВЕРУ (2026-09-13).
 *
 * client_id выдаёт конкретный MAS через динамическую регистрацию. Подсунуть
 * проду client_id, выданный стендом, — сломать вход так, что это выглядит
 * как «приложение сломалось», а не как «перепутаны сервера».
 */
function lsKey(name: string): string {
  return `${serverStoragePrefix()}.${name}`;
}

/** In-flight authorization-code flow, persisted across the redirect to MAS. */
interface FlowContext {
  state: string;
  clientId: string;
  deviceId: string;
  redirectUri: string;
  codeVerifier: string;
}

function randomHex(bytes: number): string {
  const a = new Uint8Array(bytes);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Stable per-installation device id.
 *
 * Anton's complaint, measured on our own server: 208 devices for 31 users,
 * ~97 never verified. The cause is that Matrix treats every login as a new
 * device. We refuse to do that — one installation of this app keeps one
 * device id for its whole life, and re-login reuses it.
 *
 * НО ИДЕНТИФИКАТОР СВОЙ НА КАЖДЫЙ СЕРВЕР (2026-09-13). Device id — это
 * запись в базе конкретного сервера; общий id на два сервера создавал бы
 * ложную связь между разными аккаунтами. Главное свойство сохранено:
 * в пределах ОДНОГО сервера повторный вход переиспользует тот же id.
 */
function installationDeviceId(): string {
  const key = lsKey("installation.device_id");
  let id = localStorage.getItem(key);
  if (!id) {
    id = "ALGW" + randomHex(6).toUpperCase();
    localStorage.setItem(key, id);
  }
  return id;
}

async function discoverAuthMetadata() {
  const probe = sdk.createClient({ baseUrl: config.homeserverUrl });
  return await probe.getAuthMetadata();
}

/**
 * Register this app with MAS via dynamic client registration (RFC 7591).
 * Verified working on the spike, so no static client needs provisioning
 * server-side. The resulting client_id is cached — MAS issues a new one per
 * registration and we do not want to accumulate them.
 */
async function ensureClientId(
  metadata: Awaited<ReturnType<typeof discoverAuthMetadata>>,
): Promise<string> {
  // ПОД CAPACITOR — application_type "native" (Антон/Момо, 2026-09-20
  // 19:08 UTC): дефолтная политика MAS запрещает localhost-URI для
  // "web" (регистрация отвечала "invalid redirect_uri, invalid
  // client_uri"), а для "native" http://localhost РАЗРЕШЁН.
  // redirect_uri при этом — тот же origin+pathname (http://localhost/),
  // меняются только application_type и client_uri (публичный адрес
  // приложения, т.к. localhost как client_uri MAS тоже отверг бы).
  // В браузере на devapp — прежнее поведение: web + origin URI.
  const native = isNativeShell();
  // Ключ кэша для native ОТДЕЛЬНЫЙ: иначе client_id, выданный под "web"
  // на этом же origin (локальная разработка в браузере на localhost),
  // переиспользовался бы под "native" с чужими redirect_uri — и MAS
  // резал бы authorize-запрос ещё до логина. Для web оставляем ПРЕЖНИЙ
  // ключ: десктоп не должен перерегистрироваться из-за этой правки.
  const cacheKey = lsKey(native ? "oidc.client_id.native" : "oidc.client_id");
  const cached = localStorage.getItem(cacheKey);
  if (cached) return cached;

  const clientId = await OAuth2.registerClient(metadata, {
    client_name: config.appName,
    client_uri: native ? config.appClientUri : config.appUri,
    redirect_uris: [config.redirectUri],
    application_type: native ? "native" : "web",
  });
  localStorage.setItem(cacheKey, clientId);
  return clientId;
}

/**
 * Scope, запрашиваемый у MAS при входе.
 *
 * ЗАЧЕМ `urn:mas:graphql:*`. Управление своими устройствами идёт через
 * MAS GraphQL. MAS 1.24.0 в `get_requester()` отбивает токен без этого scope
 * с 401 `Missing urn:mas:graphql:* scope`, и любая операция с устройствами
 * (и «выйти здесь», и «отключить другое устройство») падает до выполнения.
 *
 * ЗАМЕРЕНО НА БОЕВОМ MAS 2026-09-25, два полных OAuth-потока подряд
 * тестовым пользователем (живые сессии не затронуты):
 *   • без scope → POST https://mas.algsuccess.com/graphql = 401
 *     `{"errors":[{"message":"Missing urn:mas:graphql:* scope"}]}`
 *   • со scope → тот же запрос = 200, viewer и viewerSession отдаются.
 * MAS вернул scope дословно — allowlist-а scope на стороне регистрации
 * клиента нет, дополнительная серверная правка не требуется.
 *
 * ЭТО ДОБАВКА, НЕ ЗАМЕНА. Строка повторяет дефолт matrix-js-sdk
 * (`lib/oauth/authorize.js` → `generateScope`) и добавляет третий токен.
 * Убирать `urn:matrix:client:api:*` или `urn:matrix:client:device:<id>` НЕЛЬЗЯ:
 * первый даёт доступ к Matrix C-S API вообще, второй — это то, как MAS
 * вообще связывает сессию с device id (см. OwnDevices: поиск по `scope_list`).
 *
 * ПЕРЕЛОГИН ОБЯЗАТЕЛЕН: scope вшит в выданный токен. Уже живущие
 * сессии нового права НЕ получат ни по refresh, ни со временем — только
 * повторный вход через authorize.
 */
export const MAS_GRAPHQL_SCOPE = "urn:mas:graphql:*";

function requestedScope(deviceId: string): string {
  return `urn:matrix:client:api:* urn:matrix:client:device:${deviceId} ${MAS_GRAPHQL_SCOPE}`;
}

/** Send the user to MAS. Returns only if something failed before redirecting. */
export async function beginLogin(): Promise<void> {
  const metadata = await discoverAuthMetadata();
  const clientId = await ensureClientId(metadata);
  const deviceId = installationDeviceId();
  const state = randomHex(16);

  const oauth = new OAuth2(metadata, {
    clientId,
    deviceId,
    redirectUri: config.redirectUri,
  });
  const url = await oauth.generateAuthorizationCodeGrantUrl(
    state,
    "query",
    undefined,
    requestedScope(deviceId),
  );

  const flow: FlowContext = {
    state,
    clientId,
    deviceId,
    redirectUri: config.redirectUri,
    codeVerifier: oauth.context.codeVerifier,
  };
  sessionStorage.setItem(lsKey("oidc.flow"), JSON.stringify(flow));
  window.location.href = url;
}

/**
 * Handle the redirect back from MAS.
 * @returns true if a session was established, false if this is not a callback.
 */
export async function completeLoginIfCallback(): Promise<boolean> {
  const q = new URLSearchParams(window.location.search);
  const error = q.get("error");
  if (error) {
    sessionStorage.removeItem(lsKey("oidc.flow"));
    throw new Error(`MAS refused authorization: ${error} — ${q.get("error_description") ?? ""}`);
  }

  const code = q.get("code");
  if (!code) return false;

  const raw = sessionStorage.getItem(lsKey("oidc.flow"));
  if (!raw) throw new Error("Got an OIDC callback but no flow context — start login again.");
  const flow: FlowContext = JSON.parse(raw);

  if (q.get("state") !== flow.state) {
    sessionStorage.removeItem(lsKey("oidc.flow"));
    throw new Error("OIDC state mismatch — aborting login.");
  }

  const metadata = await discoverAuthMetadata();
  const oauth = new OAuth2(metadata, flow);
  const token = await oauth.completeAuthorizationCodeGrant(code);

  saveSession({
    accessToken: token.access_token,
    refreshToken: token.refresh_token,
    deviceId: flow.deviceId,
    clientId: flow.clientId,
    // MAS отдаёт expires_in (у нас 300 с — замерено). Запоминаем момент
    // истечения: без него proactive-refresh перед звонком невозможен, и
    // через 5 минут после логина вход в звонок ловил 401 (баг 14.09).
    accessTokenExpiresAt: token.expires_in
      ? Date.now() + token.expires_in * 1000
      : undefined,
  });

  sessionStorage.removeItem(lsKey("oidc.flow"));
  history.replaceState({}, "", config.redirectUri);
  return true;
}

/**
 * Build a synced MatrixClient from the stored session.
 *
 * MAS access tokens expire after 300 seconds — measured, not assumed. Without
 * `tokenRefreshFunction` the client dies with M_UNKNOWN_TOKEN at exactly five
 * minutes, which is the single easiest way to ship a broken client.
 */

/* ---- Proactive refresh (баг 14.09: 401 при входе в звонок) --------------
 *
 * КОРЕНЬ БАГА (диагноз Момо 14.09 по логам lk-jwt): getOpenIdToken() в
 * rtc.ts вызывался на протухшем access-токене MAS (живёт 300 с). Авто-
 * refresh внутри matrix-js-sdk НЕ срабатывал по двум причинам сразу:
 *   1. eager-refresh в SDK опирается на expiry, а оно становится известно
 *      только ПОСЛЕ первого refresh — на свежей сессии его нет вовсе;
 *   2. при 401 от MAS-backed Synapse ошибка не всегда имеет errcode
 *      M_UNKNOWN_TOKEN — ветка авто-retry SDK тогда не запускается.
 * Поэтому здесь экспортируется ensureFreshAccessToken(): rtc.ts обязан
 * позвать её ПЕРЕД getOpenIdToken(), а при 401 — один прозрачный повтор
 * с force-refresh.
 *
 * ГОНКА (прямое требование постановки): два одновременных входа в звонок
 * не должны запустить два refresh. Один общий inflight-promise на весь
 * модуль; внутри oauth-TokenRefresher тоже дедуплицирует — двойная
 * страховка, а не двойной запрос.
 */

/** Живой клиент и его refresher; выставляются в createSyncedClient. */
let activeClient: sdk.MatrixClient | null = null;
let activeRefresher: TokenRefresher | null = null;
/** Единственный in-flight refresh на всё приложение. */
let inflightRefresh: Promise<void> | null = null;

/**
 * Гарантировать свежий access-токен MAS.
 *
 * - expiry неизвестен (старая сохранённая сессия без поля) и force не
 *   просили — НИЧЕГО не делаем: наугад refresh'ить нельзя (расход
 *   refresh-токена), а запасной путь «retry по 401» в rtc.ts остаётся.
 * - до истечения больше withinMs (по умолчанию 60 с) — выходим сразу.
 * - иначе (или force) — refresh через общий promise. После успеха
 *   подставляем НОВЫЙ токен в http-слой живого клиента: иначе SDK
 *   продолжил бы ходить со старым, и наш refresh был бы бесполезен.
 *
 * Токен никуда не логируем (правило постановки) — только факт и сроки.
 */
export async function ensureFreshAccessToken(
  opts: { force?: boolean; withinMs?: number } = {},
): Promise<void> {
  const { force = false, withinMs = 60_000 } = opts;
  const session = loadSession();
  if (!session || !session.refreshToken || !activeRefresher || !activeClient) return;

  if (!force) {
    const expiry = session.accessTokenExpiresAt;
    if (!expiry) return;
    if (expiry - Date.now() > withinMs) return;
  }

  if (!inflightRefresh) {
    inflightRefresh = (async () => {
      const refreshToken = session.refreshToken;
      if (!refreshToken || !activeRefresher) return;
      const tokens = await activeRefresher.tokenRefreshFunction(refreshToken);
      // Подставляем свежий токен в http-слой клиента — иначе следующий
      // getOpenIdToken() уйдёт со старым, и весь refresh впустую.
      const http = (activeClient as sdk.MatrixClient | null)?.http;
      if (http) {
        (http.opts as { accessToken?: string; refreshToken?: string }).accessToken =
          tokens.accessToken;
        if (tokens.refreshToken) {
          (http.opts as { accessToken?: string; refreshToken?: string }).refreshToken =
            tokens.refreshToken;
        }
      }
      // onRefresh внутри TokenRefresher уже пересохранил сессию, но expiry
      // приходит из refresh-ответа SDK — дублируем её в наш StoredSession,
      // чтобы следующий ensureFreshAccessToken снова знал срок годности.
      const current = loadSession();
      if (current) {
        saveSession({
          ...current,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken ?? current.refreshToken,
          accessTokenExpiresAt: tokens.expiry?.getTime(),
        });
      }
    })().finally(() => {
      inflightRefresh = null;
    });
  }
  return inflightRefresh;
}

export async function createSyncedClient(): Promise<sdk.MatrixClient | null> {
  const session = loadSession();
  if (!session) return null;

  // Новая сессия — новое право быть выкинутым. Флаг дедупликации
  // модульный, и без сброса второй принудительный разлогин в той же
  // загрузке страницы был бы проигнорирован и человек снова остался бы
  // в мёртвом UI.
  forcedLogoutHandled = false;

  const probe = sdk.createClient({
    baseUrl: config.homeserverUrl,
    accessToken: session.accessToken,
    refreshToken: session.refreshToken,
    deviceId: session.deviceId,
  });

  // БАГ АНТОНА (2026-09-17 14:35): «перезагрузка компьютера вызывает разрыв
  // сессии». ПРИЧИНА: whoami() шёл сохранённым access-токеном, а он у MAS
  // живёт 5 минут. Приложение, закрытое дольше пяти минут (перезагрузка,
  // ⌘Q), при старте ловило 401 «Token is not active», и мы СТИРАЛИ сессию,
  // хотя refresh-токен был живой — его просто не спрашивали.
  // ТЕПЕРЬ: при отказе — сначала обновить токены по refresh-токену и
  // повторить whoami. Сессия стирается только если сервер отверг сам
  // refresh-токен (invalid_grant) — это и есть настоящий «разлогин».
  let who: { user_id: string; device_id?: string };
  let accessToken = session.accessToken;
  let refreshToken = session.refreshToken;
  try {
    who = await probe.whoami();
  } catch (e) {
    const err = e as sdk.MatrixError;
    const authFailure = err?.httpStatus === 401 || err?.errcode === "M_UNKNOWN_TOKEN";
    if (!authFailure) {
      // Сеть/сервер недоступны при старте (Wi-Fi ещё не поднялся после
      // перезагрузки) — это НЕ отказ сервера, сессию не трогаем.
      throw new Error(`Stored session could not be verified: ${(e as Error).message}`);
    }
    if (!session.refreshToken) {
      clearSession();
      throw new Error(`Stored session rejected by the server: ${(e as Error).message}`);
    }
    try {
      const metadata0 = await discoverAuthMetadata();
      const oauth0 = new OAuth2(metadata0, {
        clientId: session.clientId,
        deviceId: session.deviceId,
        redirectUri: config.redirectUri,
      });
      const bootRefresher = new TokenRefresher(oauth0, async () => {});
      const tokens = await bootRefresher.tokenRefreshFunction(session.refreshToken);
      accessToken = tokens.accessToken;
      refreshToken = tokens.refreshToken ?? session.refreshToken;
      saveSession({
        ...session,
        accessToken,
        refreshToken,
        accessTokenExpiresAt: tokens.expiry?.getTime(),
      });
      const probe2 = sdk.createClient({
        baseUrl: config.homeserverUrl,
        accessToken,
        refreshToken,
        deviceId: session.deviceId,
      });
      who = await probe2.whoami();
    } catch (e2) {
      clearSession();
      throw new Error(`Stored session rejected by the server: ${(e2 as Error).message}`);
    }
  }
  const session1 = loadSession() ?? { ...session, accessToken, refreshToken };

  const metadata = await discoverAuthMetadata();
  const oauth = new OAuth2(metadata, {
    clientId: session.clientId,
    deviceId: who.device_id ?? session.deviceId,
    redirectUri: config.redirectUri,
  });

  const refresher = new TokenRefresher(
    oauth,
    async (tokens: { accessToken: string; refreshToken?: string; expiry?: Date }) => {
      const current = loadSession();
      if (!current) return;
      saveSession({
        ...current,
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken ?? current.refreshToken,
        // Запоминаем срок годности нового токена: без него proactive-
        // refresh перед звонком слеп (баг 14.09 — 401 через 5 минут).
        accessTokenExpiresAt: tokens.expiry?.getTime(),
      });
    },
  );

  const client = sdk.createClient({
    baseUrl: config.homeserverUrl,
    accessToken: session1.accessToken,
    refreshToken: session1.refreshToken,
    deviceId: who.device_id ?? session.deviceId,
    userId: who.user_id,
    store: new sdk.MemoryStore(),
    tokenRefreshFunction: refresher.tokenRefreshFunction,
  });

  // Регистрируем живую связку клиент+refresher для ensureFreshAccessToken
  // (proactive refresh перед звонком, баг 14.09). Перезапись при повторном
  // входе — норма: старым клиентом уже никто не пользуется.
  activeClient = client;
  activeRefresher = refresher;

  // ЗАДАЁМ ПОНЯТНЫЙ display_name устройству (Антон/Момо 2026-09-12):
  // склейка устройств в кабинете идёт по display_name, и «Mac · alg(success)
  // (веб)» гарантирует одну запись на связку «машина + наш
  // веб» вместо записи на каждый перелогин. PUT идемпотентен и дешёв;
  // делаем best-effort, не блокируя старт приложения: имя — не вход.
  void client
    .setDeviceDetails(who.device_id ?? session.deviceId, {
      display_name: serveDeviceDisplayName(),
    })
    .catch(() => {
      /* имя не выставилось (сеть/сессия): кабинет покажет безымянным,
         переименование доступно вручную — не критично. */
    });

  return client;
}

export function logout(): void {
  activeClient = null;
  activeRefresher = null;
  clearSession();
}

/**
 * ПРИНУДИТЕЛЬНЫЙ РАЗЛОГИН — сессию завершили НА СЕРВЕРЕ (2026-09-26).
 *
 * БАГ АНТОНА (скриншот, Mac-клиент): после `mas-cli manage kill-sessions`
 * приложение оставалось открытым и выглядело живым — список комнат рисовался
 * из кеша, вместо экрана входа висела красная строка «History did not finish
 * loading: MatrixError [401] Token is not active». Человек думал, что он в
 * системе, а доступа к серверу уже не было.
 *
 * ПРИЧИНА: обработка 401 была ТОЛЬКО на старте (createSyncedClient). После
 * успешного старта никто не слушал отказ сервера: grep по src/ на
 * `Session.logged_out` давал ноль совпадений.
 *
 * ЧТО СЛУШАЕМ: `HttpApiEvent.SessionLoggedOut` ("Session.logged_out"),
 * matrix-js-sdk 42.3.0. SDK бросает его ТОЛЬКО когда сервер отверг сами
 * токены, и это ключевое свойство для пункта «сеть не должна выкидывать»:
 *   - http-api/fetch.js:145 — событие идёт лишь для MatrixError с
 *     errcode M_UNKNOWN_TOKEN, и лишь при исходе TokenRefreshOutcome.Logout;
 *   - http-api/refresh.js:139-146 — Logout ставится, если refresh упал с
 *     MatrixError/TokenRefreshLogoutError (т.е. 4xx на refresh_token grant,
 *     oauth/tokenRefresher.js:39-43 + shouldLogoutOnError). Любая другая
 *     ошибка (обрыв сети, DNS, 5xx) даёт TokenRefreshOutcome.Failure,
 *     событие НЕ бросается, сессия остаётся жить.
 * Отвалившийся Wi-Fi поэтому оставляет клиент открытым — как и требуется.
 *
 * Дедупликация: 401 прилетает сразу на несколько параллельных запросов
 * (sync, загрузка истории, пушер). Выходим один раз.
 */
let forcedLogoutHandled = false;

export function onSessionLoggedOut(
  client: sdk.MatrixClient,
  handler: () => void,
): () => void {
  const listener = (): void => {
    if (forcedLogoutHandled) return;
    forcedLogoutHandled = true;
    handler();
  };
  client.on(sdk.HttpApiEvent.SessionLoggedOut, listener);
  return () => {
    client.off(sdk.HttpApiEvent.SessionLoggedOut, listener);
  };
}

export type { StoredSession };
